<template>
  <div class="flex flex-col justify-center relative h-screen w-full bg-cover bg-no-repeat">
    <Background />

    <!-- Edit Mode -->
    <div
      v-if="editMode"
      class="flex flex-row h-full"
    >
      <!-- Edit Sidebar -->

      <!-- Edit Canvas -->
      <div class="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div class="max-w-7xl mx-auto">
          <div class="mb-4 flex items-center justify-between">
            <h2 class="text-xl font-bold text-gray-900">
              Dashboard Layout
            </h2>
            <div class="flex flex-end gap-2">
              <Button
                icon="pi pi-plus"
                label="Add Row"
                size="small"
                outlined
                @click="addRow"
              />
              <Button
                label="Save Layout"
                :loading="saving"
                @click="saveLayout"
              />
            </div>
          </div>

          <div class="space-y-4">
            <div
              v-for="(row, rowIndex) in dashboardLayout"
              :key="rowIndex"
              class="bg-white rounded-lg border-2 border-dashed border-gray-300 p-4"
            >
              <div class="flex items-center justify-between mb-3">
                <span class="text-sm font-medium text-gray-700">
                  Row {{ rowIndex + 1 }}
                </span>
                <div class="flex gap-2">
                  <Button
                    icon="pi pi-trash"
                    size="small"
                    text
                    severity="danger"
                    @click="removeRow(rowIndex)"
                  />
                </div>
              </div>

              <div
                class="grid gap-4"
                :class="getGridClass(row)"
              >
                <div
                  v-for="(widgetId, widgetIndex) in row.widgets"
                  :key="widgetIndex"
                  class="relative group"
                >
                  <div class="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 min-h-[200px]">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-gray-700">
                        {{ getWidgetName(widgetId) }}
                      </span>
                      <Button
                        icon="pi pi-times"
                        size="small"
                        text
                        rounded
                        severity="danger"
                        @click="removeWidgetFromRow(rowIndex, widgetIndex)"
                      />
                    </div>
                    <div class="text-xs text-gray-500 mb-2">
                      Preview
                    </div>
                    <!-- Widget Preview -->
                    <div class="opacity-50 pointer-events-none">
                      <DashboardCA v-if="widgetId === DashboardWidgetType.CA" />
                      <DashboardAdminNoticeboard v-else-if="widgetId === DashboardWidgetType.NOTICEBOARD" />
                      <DashboardTeamChat v-else-if="widgetId === DashboardWidgetType.TEAM_CHAT" />
                      <DashboardUpcomingEvents v-else-if="widgetId === DashboardWidgetType.UPCOMING_EVENTS" />
                      <DashboardTasks v-else-if="widgetId === DashboardWidgetType.TASKS" />
                      <DashboardVdrRequests v-else-if="widgetId === DashboardWidgetType.VDR_REQUESTS" />
                      <DashboardCIMAnalytic v-else-if="widgetId === DashboardWidgetType.CIM_ANALYTICS" />
                      <DashboardBusinessTimeline v-else-if="widgetId === DashboardWidgetType.BUSINESS_TIMELINE" />
                      <DashboardBusinessAnalyticsPipelineStatusChart v-else-if="widgetId === DashboardWidgetType.PIPELINE_STATUS" />
                      <DashboardBusinessAnalyticsDocumentStatusSummary v-else-if="widgetId === DashboardWidgetType.DOC_STATUS_SUMMARY" />
                      <DashboardBusinessAnalyticsDocumentStatusChart v-else-if="widgetId === DashboardWidgetType.DOC_STATUS_CHART" />
                      <DashboardBusinessAnalyticsNewBusinessesByMonthChart v-else-if="widgetId === DashboardWidgetType.NEW_BUSINESSES_MONTH" />
                      <DashboardBusinessAnalyticsSoldBusinessesByMonthChart v-else-if="widgetId === DashboardWidgetType.SOLD_BUSINESSES_MONTH" />
                      <DashboardBusinessAnalyticsBusinessesByAgentChart v-else-if="widgetId === DashboardWidgetType.BUSINESSES_BY_AGENT" />
                      <DashboardCommissionAnalyticsSummary v-else-if="widgetId === DashboardWidgetType.COMMISSION_SUMMARY" />
                      <DashboardCommissionAnalyticsCommissionByAgentChart v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_AGENT" />
                      <DashboardCommissionAnalyticsCommissionByMonthChart v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_MONTH" />
                      <DashboardCommissionAnalyticsCommissionByStageChart v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_STAGE" />
                      <DashboardCommissionAnalyticsCommissionByStatusChart v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_STATUS" />
                      <DashboardCommissionAnalyticsOfficeVsAgentBreakdown v-else-if="widgetId === DashboardWidgetType.OFFICE_VS_AGENT" />
                      <DashboardIndustryAnalyticsSummary v-else-if="widgetId === DashboardWidgetType.INDUSTRY_SUMMARY" />
                      <DashboardIndustryAnalyticsTotalBusinessesChart v-else-if="widgetId === DashboardWidgetType.TOTAL_BUSINESSES_CHART" />
                      <DashboardIndustryAnalyticsTotalPriceChart v-else-if="widgetId === DashboardWidgetType.TOTAL_PRICE_CHART" />
                      <DashboardIndustryAnalyticsAveragePriceChart v-else-if="widgetId === DashboardWidgetType.AVERAGE_PRICE_CHART" />
                      <DashboardIndustryAnalyticsAverageMultipleChart v-else-if="widgetId === DashboardWidgetType.AVERAGE_MULTIPLE_CHART" />
                    </div>
                  </div>
                </div>
                <div
                  v-if="row.widgets.length === 0 || (row.widgets.length < 2 && row.size === 1)"
                  class="border-2 border-dashed border-gray-300 rounded-lg p-4 min-h-[200px] flex items-center justify-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
                  @click="openWidgetSelector(rowIndex)"
                >
                  <div class="text-center">
                    <Icon
                      name="mdi:plus-circle-outline"
                      class="text-gray-400 mx-auto mb-2"
                      size="32"
                    />
                    <p class="text-sm text-gray-500">
                      {{ row.widgets.length === 0 ? 'Add Widget' : 'Add Another Widget' }}
                    </p>
                    <p
                      v-if="row.widgets.length > 0"
                      class="text-xs text-gray-400 mt-1"
                    >
                      (1 column widgets only)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Normal View -->
    <div
      v-else
      class="flex flex-col h-full"
    >
      <div class="h-full p-10 mb-10 flex flex-col gap-4 overflow-y-auto">
        <div class="flex items-center justify-between gap-4 h-fit">
          <span class="text-2xl font-bold text-primary">
            {{ greetingMessage }}
          </span>

          <div class="hidden sm:block">
            <Button
              icon="pi pi-pencil"
              rounded
              label="Customize Dashboard"
              aria-label="Edit Dashboard"
              @click="editMode = true"
            />
          </div>
        </div>

        <div
          v-if="dashboardLayout.length > 0"
          class="space-y-6"
        >
          <div
            v-for="(row, rowIndex) in dashboardLayout"
            :key="rowIndex"
            class="grid gap-6"
            :class="getGridClass(row)"
          >
            <template
              v-for="(widgetId, widgetIndex) in row.widgets"
              :key="widgetIndex"
            >
              <LazyVisibleDashboardCA
                v-if="widgetId === DashboardWidgetType.CA"
                hydrate-on-visible
                class="min-h-[300px] max-h-[300px] overflow-hidden"
              />
              <LazyVisibleDashboardAdminNoticeboard
                v-else-if="widgetId === DashboardWidgetType.NOTICEBOARD"
                hydrate-on-visible
                class="min-h-[300px] max-h-[300px] overflow-hidden"
              />
              <LazyVisibleDashboardTeamChat
                v-else-if="widgetId === DashboardWidgetType.TEAM_CHAT"
                hydrate-on-visible
                class="min-h-[300px] max-h-[300px] overflow-hidden"
              />
              <LazyVisibleDashboardUpcomingEvents
                v-else-if="widgetId === DashboardWidgetType.UPCOMING_EVENTS"
                hydrate-on-visible
                class="min-h-[300px] max-h-[300px] overflow-hidden"
              />
              <LazyVisibleDashboardTasks
                v-else-if="widgetId === DashboardWidgetType.TASKS"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardVdrRequests
                v-else-if="widgetId === DashboardWidgetType.VDR_REQUESTS"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardCIMAnalytic
                v-else-if="widgetId === DashboardWidgetType.CIM_ANALYTICS"
                hydrate-on-visible
                class="min-h-[900px] max-h-[1200px] overflow-hidden"
              />
              <LazyVisibleDashboardBusinessTimeline
                v-else-if="widgetId === DashboardWidgetType.BUSINESS_TIMELINE"
                hydrate-on-visible
                class="min-h-[500px] max-h-[500px] overflow-hidden"
              />
              <LazyVisibleDashboardBusinessAnalyticsPipelineStatusChart
                v-else-if="widgetId === DashboardWidgetType.PIPELINE_STATUS"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardBusinessAnalyticsDocumentStatusSummary
                v-else-if="widgetId === DashboardWidgetType.DOC_STATUS_SUMMARY"
                hydrate-on-visible
                class="min-h-[170px] max-h-[170px] overflow-hidden"
              />
              <LazyVisibleDashboardBusinessAnalyticsDocumentStatusChart
                v-else-if="widgetId === DashboardWidgetType.DOC_STATUS_CHART"
                hydrate-on-visible
                class="min-h-[450px] max-h-[450px] overflow-hidden"
              />
              <LazyVisibleDashboardBusinessAnalyticsNewBusinessesByMonthChart
                v-else-if="widgetId === DashboardWidgetType.NEW_BUSINESSES_MONTH"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardBusinessAnalyticsSoldBusinessesByMonthChart
                v-else-if="widgetId === DashboardWidgetType.SOLD_BUSINESSES_MONTH"
                hydrate-on-visible
                class="h-[300px] max-h-[300px] overflow-hidden"
              />
              <LazyVisibleDashboardBusinessAnalyticsBusinessesByAgentChart
                v-else-if="widgetId === DashboardWidgetType.BUSINESSES_BY_AGENT"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardCommissionAnalyticsSummary
                v-else-if="widgetId === DashboardWidgetType.COMMISSION_SUMMARY"
                hydrate-on-visible
                class="h-[200px] max-h-[200px] overflow-hidden"
              />
              <LazyVisibleDashboardCommissionAnalyticsCommissionByAgentChart
                v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_AGENT"
                hydrate-on-visible
                class="h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardCommissionAnalyticsCommissionByMonthChart
                v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_MONTH"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardCommissionAnalyticsCommissionByStageChart
                v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_STAGE"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardCommissionAnalyticsCommissionByStatusChart
                v-else-if="widgetId === DashboardWidgetType.COMMISSION_BY_STATUS"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardCommissionAnalyticsOfficeVsAgentBreakdown
                v-else-if="widgetId === DashboardWidgetType.OFFICE_VS_AGENT"
                hydrate-on-visible
                class="min-h-[500px] max-h-[700px] overflow-hidden"
              />
              <LazyVisibleDashboardIndustryAnalyticsSummary
                v-else-if="widgetId === DashboardWidgetType.INDUSTRY_SUMMARY"
                hydrate-on-visible
                class="h-[200px] max-h-[200px] overflow-hidden"
              />
              <LazyVisibleDashboardIndustryAnalyticsTotalBusinessesChart
                v-else-if="widgetId === DashboardWidgetType.TOTAL_BUSINESSES_CHART"
                hydrate-on-visible
                class="h-[500px] max-h-[500px] overflow-hidden"
              />
              <LazyVisibleDashboardIndustryAnalyticsTotalPriceChart
                v-else-if="widgetId === DashboardWidgetType.TOTAL_PRICE_CHART"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardIndustryAnalyticsAveragePriceChart
                v-else-if="widgetId === DashboardWidgetType.AVERAGE_PRICE_CHART"
                hydrate-on-visible
                class="min-h-[400px] max-h-[400px] overflow-hidden"
              />
              <LazyVisibleDashboardIndustryAnalyticsAverageMultipleChart
                v-else-if="widgetId === DashboardWidgetType.AVERAGE_MULTIPLE_CHART"
                hydrate-on-visible
                class="min-h-[500px] max-h-[500px] overflow-hidden"
              />
            </template>
          </div>
        </div>

        <div
          v-else-if="loading"
          class="flex items-center justify-center h-64 text-gray-500"
        >
          <div class="text-center">
            <i class="pi pi-spin pi-spinner text-2xl mb-2" />
            <p class="text-sm">
              Loading dashboard...
            </p>
          </div>
        </div>

        <div
          v-else
          class="flex items-center justify-center h-64 text-gray-500"
        >
          <div class="text-center">
            <p class="text-lg font-medium mb-2">
              No widgets configured
            </p>
            <p class="text-sm">
              Click the edit button to add widgets to your dashboard
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Widget Customizer Dialog -->
    <DashboardWidgetCustomizer
      v-model:visible="showCustomizer"
    />

    <!-- Widget Selector Sidebar -->
    <Drawer
      v-model:visible="showWidgetSelector"
      position="right"
      :style="{ width: '600px' }"
    >
      <template #header>
        <div class="flex flex-col gap-2">
          <h3 class="text-lg font-semibold text-gray-900">
            Select Widget
          </h3>
          <p class="text-sm text-gray-500">
            {{ selectorRowIndex !== null && dashboardLayout[selectorRowIndex]?.widgets.length > 0 ? 'Select a 1-column widget to add' : 'Select a widget to add to this row' }}
          </p>
        </div>
      </template>

      <div class="space-y-2">
        <div
          v-for="widget in getAvailableWidgetsForRow(selectorRowIndex)"
          :key="widget.typeKey"
          class="p-3 border rounded-lg transition-colors cursor-pointer"
          :class="[
            isWidgetInLayout(widget.typeKey)
              ? 'border-gray-300 bg-gray-100 opacity-60 cursor-not-allowed'
              : 'border-gray-200 hover:bg-gray-50 hover:border-primary-300',
          ]"
          @click="!isWidgetInLayout(widget.typeKey) && addWidgetToRow(selectorRowIndex, widget.typeKey)"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <p class="text-sm font-medium text-gray-900">
                  {{ widget.name }}
                </p>
                <Tag
                  :value="widget.minsize === 1 ? '1 Col' : '2 Cols'"
                  :severity="widget.minsize === 1 ? 'success' : 'warning'"
                  size="small"
                />
              </div>
              <p
                v-if="isWidgetInLayout(widget.typeKey)"
                class="text-xs text-gray-500"
              >
                Already added
              </p>
              <p
                v-else-if="widget.minsize === 2"
                class="text-xs text-gray-500"
              >
                Requires full row
              </p>
            </div>
            <Icon
              v-if="!isWidgetInLayout(widget.typeKey)"
              name="mdi:plus-circle"
              class="text-primary-500"
              size="20"
            />
            <Icon
              v-else
              name="mdi:check-circle"
              class="text-gray-400"
              size="20"
            />
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import type { DashboardWidget, DashboardRow } from '~/composables/useDashboardLayout'
import { DashboardWidgetType } from '~/composables/useDashboardLayout'

const {
  loggedInUser,
} = useSidebar()

const {
  getEnabledWidgets,
  toggleWidget,
  saveDashboardLayout,
  getDashboardLayout,
  loading,
  saving,
} = useDashboardLayout()

const showCustomizer = ref(false)
const editMode = ref(false)
const showWidgetSelector = ref(false)
const selectorRowIndex = ref<number | null>(null)

const dashboardLayout = ref<DashboardRow[]>([])

const availableWidgets = computed(() => {
  const allWidgets = [
    { name: 'CA\'s Requiring Qualification', typeKey: DashboardWidgetType.CA, minsize: 1 },
    { name: 'Admin Noticeboard', typeKey: DashboardWidgetType.NOTICEBOARD, minsize: 1 },
    { name: 'Team Chat', typeKey: DashboardWidgetType.TEAM_CHAT, minsize: 1 },
    { name: 'Upcoming Events', typeKey: DashboardWidgetType.UPCOMING_EVENTS, minsize: 1 },
    { name: 'My Tasks', typeKey: DashboardWidgetType.TASKS, minsize: 1 },
    { name: 'VDR Requests', typeKey: DashboardWidgetType.VDR_REQUESTS, minsize: 1 },
    { name: 'CIM Analytics', typeKey: DashboardWidgetType.CIM_ANALYTICS, minsize: 2 },
    { name: 'Business Timeline', typeKey: DashboardWidgetType.BUSINESS_TIMELINE, minsize: 2 },
    { name: 'Pipeline Status Chart', typeKey: DashboardWidgetType.PIPELINE_STATUS, minsize: 2 },
    { name: 'Document Status Summary', typeKey: DashboardWidgetType.DOC_STATUS_SUMMARY, minsize: 1 },
    { name: 'Document Status Chart', typeKey: DashboardWidgetType.DOC_STATUS_CHART, minsize: 2 },
    { name: 'New Businesses by Month', typeKey: DashboardWidgetType.NEW_BUSINESSES_MONTH, minsize: 2 },
    { name: 'Sold Businesses by Month', typeKey: DashboardWidgetType.SOLD_BUSINESSES_MONTH, minsize: 2 },
    { name: 'Businesses by Agent', typeKey: DashboardWidgetType.BUSINESSES_BY_AGENT, minsize: 2 },
    { name: 'Commission Summary', typeKey: DashboardWidgetType.COMMISSION_SUMMARY, minsize: 1 },
    { name: 'Commission by Agent', typeKey: DashboardWidgetType.COMMISSION_BY_AGENT, minsize: 2 },
    { name: 'Commission by Month', typeKey: DashboardWidgetType.COMMISSION_BY_MONTH, minsize: 2 },
    { name: 'Commission by Stage', typeKey: DashboardWidgetType.COMMISSION_BY_STAGE, minsize: 2 },
    { name: 'Commission by Status', typeKey: DashboardWidgetType.COMMISSION_BY_STATUS, minsize: 2 },
    { name: 'Office vs Agent Breakdown', typeKey: DashboardWidgetType.OFFICE_VS_AGENT, minsize: 2 },
    { name: 'Industry Summary', typeKey: DashboardWidgetType.INDUSTRY_SUMMARY, minsize: 1 },
    { name: 'Total Businesses Chart', typeKey: DashboardWidgetType.TOTAL_BUSINESSES_CHART, minsize: 2 },
    { name: 'Total Price Chart', typeKey: DashboardWidgetType.TOTAL_PRICE_CHART, minsize: 2 },
    { name: 'Average Price Chart', typeKey: DashboardWidgetType.AVERAGE_PRICE_CHART, minsize: 2 },
    { name: 'Average Multiple Chart', typeKey: DashboardWidgetType.AVERAGE_MULTIPLE_CHART, minsize: 2 },
  ]
  return allWidgets.sort((a, b) => a.name.localeCompare(b.name))
})

const getGridClass = (row: DashboardRow) => {
  if (row.widgets.length === 1 && row.size === 2) {
    return 'grid-cols-1'
  }
  return 'grid-cols-1 sm:grid-cols-2'
}

const getWidgetName = (widgetId: string) => {
  const widget = availableWidgets.value.find(w => w.typeKey === widgetId)
  return widget?.name || widgetId
}

const addRow = () => {
  dashboardLayout.value.push({ widgets: [], size: 1 })
}

const removeRow = (index: number) => {
  if (dashboardLayout.value.length > 1) {
    dashboardLayout.value.splice(index, 1)
  }
}

const removeWidgetFromRow = (rowIndex: number, widgetIndex: number) => {
  dashboardLayout.value[rowIndex].widgets.splice(widgetIndex, 1)
  if (dashboardLayout.value[rowIndex].widgets.length === 0) {
    dashboardLayout.value.splice(rowIndex, 1)
  }
}

const openWidgetSelector = (rowIndex: number) => {
  selectorRowIndex.value = rowIndex
  showWidgetSelector.value = true
}

const getAvailableWidgetsForRow = (rowIndex: number | null) => {
  if (rowIndex === null) return availableWidgets.value

  const row = dashboardLayout.value[rowIndex]
  if (!row) return availableWidgets.value

  const usedWidgets = dashboardLayout.value.flatMap(r => r.widgets)

  return availableWidgets.value.filter((w) => {
    if (usedWidgets.includes(w.typeKey)) return false

    if (row.widgets.length > 0) {
      return w.minsize === 1
    }

    return true
  })
}

const addWidgetToRow = (rowIndex: number | null, widgetId: string) => {
  if (rowIndex === null) return

  const row = dashboardLayout.value[rowIndex]
  if (!row) return

  const widget = availableWidgets.value.find(w => w.typeKey === widgetId)
  if (!widget) return

  if (isWidgetInLayout(widgetId)) return

  if (widget.minsize === 2) {
    dashboardLayout.value[rowIndex] = {
      widgets: [widgetId],
      size: 2,
    }
  }
  else {
    if (row.widgets.length < 2 && row.size === 1) {
      dashboardLayout.value[rowIndex].widgets.push(widgetId)
      dashboardLayout.value[rowIndex].size = 1
    }
  }

  showWidgetSelector.value = false
  selectorRowIndex.value = null
}

const isWidgetInLayout = (widgetId: string) => {
  return dashboardLayout.value.some(row => row.widgets.includes(widgetId))
}

const saveLayout = async () => {
  try {
    await saveDashboardLayout(dashboardLayout.value)

    dashboardLayout.value.forEach((row) => {
      row.widgets.forEach((widgetId) => {
        const widget = getEnabledWidgets.value.find((w: DashboardWidget) => w.id === widgetId)
        if (widget && !widget.enabled) {
          toggleWidget(widgetId)
        }
      })
    })
    editMode.value = false
  }
  catch (error) {
    console.error('Error saving dashboard layout:', error)
  }
}

onMounted(async () => {
  try {
    const response = await getDashboardLayout()
    if (response.data && Array.isArray(response.data) && response.data.length > 0) {
      dashboardLayout.value = response.data as unknown as DashboardRow[]
    }
  }
  catch (error) {
    console.error('Error loading dashboard layout:', error)
  }
})

const greetingMessage = computed(() => {
  const currentHour = new Date().getHours()

  let greeting = ''
  if (currentHour < 12) {
    greeting = 'Good Morning'
  }
  else if (currentHour < 18) {
    greeting = 'Good Afternoon'
  }
  else {
    greeting = 'Good Evening'
  }

  return `${greeting} ${loggedInUser?.value?.name || ''}`
})
</script>
